import { DateTime, Interval } from 'luxon';
import { useCallback, useMemo } from 'react';
import { TimeValueDataPoint } from '../../components/funds-robot/comparison-screen/comparison-chart/ValueGrowthChart';
import { Stock } from '../../types/Stock';
import {
    dividendsWithinInterval,
    getAnnualReturn,
    indexedFundTimeValueDataPoints,
    timeValueDataPointsWithinInterval,
} from '../../utils/functions';
import { useGetFundRates } from './getFundRates';

export const useGetYearlyReturnsFund = (
    startDate: DateTime | Date,
    id?: string,
    dividends?: Stock['dividendPayments'],
    givenInterval?: Interval,
) => {
    const fundRatesQuery = useGetFundRates(id);

    const timeValueDataPoints = useMemo(() => {
        return (
            fundRatesQuery.data?.rates.map((r) => {
                return {
                    t: DateTime.fromISO(r.date).toMillis(),
                    y: r.nav * 100, // Nav rates are not returned in cents from the backend, but in euros. Convert to cents.
                };
            }) || []
        );
    }, [fundRatesQuery.data]);

    const intervalThisYear = Interval.fromDateTimes(DateTime.now().startOf('year'), DateTime.now());
    const intervalSinceStart =
        timeValueDataPoints[0] && Interval.fromDateTimes(startDate, DateTime.fromMillis(timeValueDataPoints[0].t));

    const getIndexedInInterval = useCallback(
        (timeValueDataPoints: TimeValueDataPoint[], interval?: Interval) => {
            const withinInterval = interval
                ? timeValueDataPointsWithinInterval(timeValueDataPoints, interval)
                : timeValueDataPoints;
            return indexedFundTimeValueDataPoints(
                withinInterval,
                interval
                    ? dividendsWithinInterval(
                          (dividends || []).map((d) => {
                              return {
                                  amount: d.amountPerParticipation,
                                  date: DateTime.fromISO(d.date),
                              };
                          }),
                          interval,
                      )
                    : (dividends || []).map((d) => {
                          return {
                              amount: d.amountPerParticipation,
                              date: DateTime.fromISO(d.date),
                          };
                      }),
            );
        },
        [dividends],
    );

    const indexedThisYear = useMemo(
        () => getIndexedInInterval(timeValueDataPoints, intervalThisYear),
        [timeValueDataPoints, intervalThisYear, getIndexedInInterval],
    );
    const indexedSinceStart = useMemo(
        () => getIndexedInInterval(timeValueDataPoints, intervalSinceStart),
        [timeValueDataPoints, intervalSinceStart, getIndexedInInterval],
    );
    const indexedGivenInterval = useMemo(
        () => getIndexedInInterval(timeValueDataPoints, givenInterval),
        [timeValueDataPoints, givenInterval, getIndexedInInterval],
    );

    // Only show annual returns when the fund existed longer than a year
    const indexedAnnual =
        intervalSinceStart?.length('years') >= 1
            ? givenInterval && indexedGivenInterval[0]
                ? getAnnualReturn(indexedGivenInterval[0].y - 100, givenInterval)
                : indexedSinceStart[0] && getAnnualReturn(indexedSinceStart[0].y - 100, intervalSinceStart)
            : undefined;
    return {
        thisYear: indexedThisYear[0] ? indexedThisYear[0].y - 100 : undefined,
        annual: indexedAnnual ? indexedAnnual : undefined,
        total: indexedSinceStart[0] ? indexedSinceStart[0].y - 100 : undefined,
    };
};
